import { lazy, type LazyExoticComponent } from 'react';

const CHUNK_RETRY_KEY = 'chunk_load_retried';

// Failed dynamic imports throw TypeError in all modern browsers.
// Other module-level failures (SyntaxError, ReferenceError, etc.) are not
// chunk-load errors and should not trigger a reload.
function isChunkLoadError(error: unknown): boolean {
  return error instanceof TypeError;
}

export function withChunkRetry<T>(
  name: string,
  factory: () => Promise<T>,
): () => Promise<T> {
  return () =>
    factory().catch((error: unknown) => {
      if (!isChunkLoadError(error)) {
        throw error;
      }

      const key = `${CHUNK_RETRY_KEY}:${name}`;

      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        // Stay suspended while the reload happens — this promise never resolves
        return new Promise<never>(() => {});
      }

      throw error;
    });
}

export function lazyWithRetry<T extends { default: React.ComponentType }>(
  name: string,
  factory: () => Promise<T>,
): LazyExoticComponent<T['default']> {
  return lazy(withChunkRetry(name, factory));
}
